import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { isStandalone } from '@/lib/pwa';

/**
 * InstallPrompt — resident PWA "Add to Home Screen" affordance.
 *
 * The web app is currently the primary resident surface (the native mobile app
 * is not yet launched), so we actively encourage installing it to the home
 * screen for app-like launch and reach.
 *
 * Two paths, because the platforms differ:
 *   - Android / Chromium: the browser fires `beforeinstallprompt`. We capture
 *     it, show a banner, and trigger the native install dialog on tap.
 *   - iOS / Safari: there is NO install API. We detect iOS Safari that is not
 *     already installed and show a short "Share → Add to Home Screen" guide.
 *
 * Dismissal is remembered in localStorage so residents are not nagged.
 */

const DISMISS_KEY = 'sl_pwa_install_dismissed';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ Safari reports a desktop Mac UA, so detect it via touch points.
  const isIpadOs =
    /macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  const isIos = /iphone|ipad|ipod/i.test(ua) || isIpadOs;
  // Exclude in-app browsers (FB, Instagram, etc.) where Add-to-Home is absent.
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

function recentlyDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / Chromium path.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Once installed, clear the banner and remember it.
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowIosGuide(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS path — no event, so decide on mount.
    if (isIosSafari()) setShowIosGuide(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferredPrompt(null);
    setShowIosGuide(false);
  }

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // Whatever the choice, don't re-show this session.
    setDeferredPrompt(null);
  }

  if (!deferredPrompt && !showIosGuide) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] p-4 shadow-[0_8px_32px_rgba(0,0,0,0.45)] sm:left-auto sm:right-4 sm:mx-0"
      role="dialog"
      aria-label="Install Secure Labs"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded-md p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <X className="size-4" />
      </button>

      <div className="flex items-start gap-3 pr-5">
        <img
          src="/icons/icon-192.png"
          alt=""
          className="size-11 shrink-0 rounded-lg"
          width={44}
          height={44}
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-primary)]">
            Add Secure Labs to your home screen
          </p>

          {deferredPrompt ? (
            <>
              <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                Launch it like an app — faster access to passes, invites and walk-ins.
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="primary" onClick={install}>
                  <Download className="size-4" />
                  Install
                </Button>
                <Button size="sm" variant="secondary" onClick={dismiss}>
                  Not now
                </Button>
              </div>
            </>
          ) : (
            <p className="mt-1 text-xs text-[var(--text-tertiary)]">
              Tap <Share className="inline size-3.5 -translate-y-px text-[var(--text-accent)]" />{' '}
              <span className="text-[var(--text-secondary)]">Share</span>, then choose{' '}
              <span className="text-[var(--text-secondary)]">“Add to Home Screen”</span>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
